import { MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $setBlocksType } from '@lexical/selection';
import { $getSelection, $isRangeSelection, $createParagraphNode } from 'lexical';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $createCodeNode } from '@lexical/code';
import {
  INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_CHECK_LIST_COMMAND,
} from '@lexical/list';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import {
  Heading1, Heading2, Heading3, Text as TextIcon, List, ListOrdered, ListChecks,
  Quote, Code2, Table as TableIcon, Image as ImageIcon, Youtube, Film,
} from 'lucide-react';
import { NEW_TABLE_CONFIG } from './tableDefaults';
import { INSERT_DOC_IMAGE_COMMAND } from './lexicalNodes/DocImageNode';
import { INSERT_DOC_VIDEO_COMMAND } from './lexicalNodes/DocVideoEmbedNode';

/**
 * Slash (/) command menu options — extracted from LexicalRichEditor.js so the
 * real option list (groups, order, commands, payloads) is testable as a pure
 * module, without dragging that file's OsScroll -> bare .css import through
 * jest (same reason EmptyLinePlaceholderPlugin moved out).
 */

/** The order groups render in. Extras with unknown groups follow these, in
 *  first-appearance order — see `buildSlashOptions`. */
export const SLASH_GROUP_ORDER = ['Basic', 'Lists', 'Media'];

export class SlashOption extends MenuOption {
  constructor(title, { group, description = '', icon, keywords = [], onSelect }) {
    super(title);
    this.title = title;
    this.group = group;
    this.description = description;
    this.icon = icon;
    this.keywords = keywords;
    this.onSelect = onSelect;
  }
}

/**
 * The full option list, GROUP-CONTIGUOUS: every option of a group is adjacent
 * to the rest of its group, so the menu can render a group header exactly
 * where `option.group` changes from the previous option. Filtering with
 * `Array.filter` preserves relative order, so contiguity survives it — no
 * re-sort needed downstream.
 *
 * `extras` lets a host surface its own commands (each carrying its own
 * `group`); they are appended after the built-ins, grouped by first
 * appearance, so a host group never splits a built-in one.
 *
 * `media` gates the Media group: only hosts that mount DocMediaPlugin (i.e.
 * pass LexicalRichEditor a `media` prop — the documents editor) get the
 * Image/YouTube items, because the commands they dispatch are handled by that
 * plugin alone. Offering them where nothing listens would be a dead menu.
 */
export function buildSlashOptions(editor, extras = [], { media = false } = {}) {
  const asBlock = (creator) => () => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) $setBlocksType(selection, creator);
  };

  const options = [
    // ---- Basic ----
    new SlashOption('Text', {
      group: 'Basic', description: 'Plain paragraph', icon: TextIcon,
      keywords: ['paragraph', 'text', 'plain'],
      onSelect: asBlock(() => $createParagraphNode()),
    }),
    new SlashOption('Heading 1', {
      group: 'Basic', description: 'Large section heading', icon: Heading1,
      keywords: ['title', 'h1', 'big'],
      onSelect: asBlock(() => $createHeadingNode('h1')),
    }),
    new SlashOption('Heading 2', {
      group: 'Basic', description: 'Medium section heading', icon: Heading2,
      keywords: ['subtitle', 'h2'],
      onSelect: asBlock(() => $createHeadingNode('h2')),
    }),
    new SlashOption('Heading 3', {
      group: 'Basic', description: 'Small section heading', icon: Heading3,
      keywords: ['h3', 'small heading'],
      onSelect: asBlock(() => $createHeadingNode('h3')),
    }),
    new SlashOption('Quote', {
      group: 'Basic', description: 'Capture a quote', icon: Quote,
      keywords: ['blockquote', 'citation'],
      onSelect: asBlock(() => $createQuoteNode()),
    }),
    new SlashOption('Code', {
      group: 'Basic', description: 'Code block with highlighting', icon: Code2,
      keywords: ['code', 'snippet', 'pre'],
      onSelect: asBlock(() => $createCodeNode()),
    }),
    new SlashOption('Table', {
      group: 'Basic', description: 'Insert a 3×3 table', icon: TableIcon,
      keywords: ['table', 'grid', 'rows', 'columns'],
      // Payload lives in tableDefaults.js so the test asserts the REAL config
      // rather than a copy of it.
      onSelect: () => editor.dispatchCommand(INSERT_TABLE_COMMAND, NEW_TABLE_CONFIG),
    }),
    // ---- Lists ----
    new SlashOption('Bulleted list', {
      group: 'Lists', description: 'Create a bulleted list', icon: List,
      keywords: ['unordered', 'ul', 'bullet'],
      onSelect: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
    }),
    new SlashOption('Numbered list', {
      group: 'Lists', description: 'Create a numbered list', icon: ListOrdered,
      keywords: ['ordered', 'ol', 'number'],
      onSelect: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
    }),
    new SlashOption('To-do list', {
      group: 'Lists', description: 'Track tasks with checkboxes', icon: ListChecks,
      keywords: ['todo', 'task', 'checkbox', 'check', 'checklist'],
      onSelect: () => editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined),
    }),
  ];

  if (media) {
    // ---- Media ----
    options.push(
      new SlashOption('Image', {
        group: 'Media', description: 'Upload from your device', icon: ImageIcon,
        keywords: ['image', 'photo', 'picture', 'upload', 'file'],
        // No payload -> DocMediaPlugin opens the OS image picker.
        onSelect: () => editor.dispatchCommand(INSERT_DOC_IMAGE_COMMAND, undefined),
      }),
      new SlashOption('Image from URL', {
        group: 'Media', description: 'Fetch a web image and store it', icon: ImageIcon,
        keywords: ['image', 'url', 'link', 'web', 'photo'],
        // {prompt:'url'} -> DocMediaPlugin opens the image-link prompt; the
        // fetched bytes go through the SAME compress->encrypt->upload
        // pipeline, so the document stores our bucket link, never the
        // foreign URL.
        onSelect: () => editor.dispatchCommand(INSERT_DOC_IMAGE_COMMAND, { prompt: 'url' }),
      }),
      new SlashOption('Video file', {
        group: 'Media', description: 'Upload a video (mp4, webm, mov — up to 48 MB)', icon: Film,
        keywords: ['video', 'upload', 'mp4', 'webm', 'recording', 'screen', 'file'],
        // {pick:'video'} -> DocMediaPlugin opens the OS picker for videos.
        onSelect: () => editor.dispatchCommand(INSERT_DOC_IMAGE_COMMAND, { pick: 'video' }),
      }),
      new SlashOption('YouTube', {
        group: 'Media', description: 'Embed a YouTube or Vimeo video', icon: Youtube,
        keywords: ['youtube', 'vimeo', 'video', 'embed'],
        // No payload -> DocMediaPlugin opens the inline URL prompt.
        onSelect: () => editor.dispatchCommand(INSERT_DOC_VIDEO_COMMAND, undefined),
      })
    );
  }

  // Extras after every built-in, kept contiguous per extras group: stable-sort
  // by first appearance of each group. Array.prototype.sort is stable, so
  // options within a group keep the order the host gave them.
  if (extras.length) {
    const firstSeen = new Map();
    extras.forEach((o, i) => {
      if (!firstSeen.has(o.group)) firstSeen.set(o.group, i);
    });
    options.push(...[...extras].sort((a, b) => firstSeen.get(a.group) - firstSeen.get(b.group)));
  }

  return options;
}

/** Case-insensitive match on title, keywords, or description. Returns the
 *  input array itself for an empty query, so referential equality holds. */
export function filterSlashOptions(options, query) {
  if (!query) return options;
  const q = query.toLowerCase();
  return options.filter(
    (o) =>
      o.title.toLowerCase().includes(q) ||
      o.keywords.some((k) => k.includes(q)) ||
      (o.description && o.description.toLowerCase().includes(q))
  );
}
